import { useEffect, useMemo } from 'react'
import { useFormStore, useFormStep, useFormData } from '@hooks/useFormStore'
import { analyticsApi } from '@services/api'
import { StepIndicator } from './StepIndicator'
import { TipoClienteSection } from './TipoClienteSection'
import { AnagraficaSection } from './AnagraficaSection'
import { DocumentoSection } from './DocumentoSection'
import { FornituraSection } from './FornituraSection'
import { PagamentoSection } from './PagamentoSection'
import { RiepilogoSection } from './RiepilogoSection'
import { OtpRequestSection, OtpVerifySection, SigningSection, SigningComplete } from '../Signing'
import { OperatorRiepilogoSection } from '../Operator'
import type { FormStep } from '@/types'
import { getFormSteps, SIGNING_STEPS } from '@/types'
import styles from './FormWrapper.module.css'

interface FormWrapperProps {
  sessionId: string | null
  mode?: 'self' | 'operator'
  onOperatorSentToClient?: (submissionId: number) => void
}

export function FormWrapper({ sessionId: _sessionId, mode = 'self', onOperatorSentToClient }: FormWrapperProps) {
  const currentStep = useFormStep()
  const formData = useFormData()
  const setStep = useFormStore((state) => state.setStep)

  const visibleSteps = useMemo(() => getFormSteps(mode), [mode])

  // Track page view
  useEffect(() => {
    analyticsApi.trackPageView('form_autocaricamento')
  }, [])

  // Recovery: se lo step persistito in sessionStorage non e' fra quelli
  // attualmente visibili (es. cambio di tipo_cliente o aggiornamento del
  // wizard) e non e' uno SIGNING_STEPS, sposta l'utente sul prossimo step
  // valido. Lo step "pagamento" e' SEMPRE visibile.
  useEffect(() => {
    if (!visibleSteps.includes(currentStep) && !SIGNING_STEPS.includes(currentStep)) {
      const allSteps = getFormSteps(mode)
      const currentIdx = allSteps.indexOf(currentStep)
      const nextVisible = visibleSteps.find(s => allSteps.indexOf(s) > currentIdx)
      if (nextVisible) setStep(nextVisible)
    }
  }, [currentStep, visibleSteps, mode, setStep])

  // Track step changes
  useEffect(() => {
    const stepIndex = visibleSteps.indexOf(currentStep)
    analyticsApi.trackFormStep(currentStep, stepIndex + 1)
  }, [currentStep, visibleSteps])

  const handleStepClick = (step: FormStep) => {
    const targetIndex = visibleSteps.indexOf(step)
    const currentIndex = visibleSteps.indexOf(currentStep)
    
    if (targetIndex < currentIndex) {
      setStep(step)
    }
  }

  const getCompletedSteps = (): FormStep[] => {
    const currentIndex = visibleSteps.indexOf(currentStep)
    return visibleSteps.slice(0, currentIndex)
  }

  const isBusiness = formData.tipo_cliente === 'business'

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'tipo_cliente':
        return <TipoClienteSection />
      case 'upload':
        // Step rimosso a maggio 2026: se un cliente arriva qui con sessione
        // persistita pre-rimozione, FormWrapper rimappera' automaticamente
        // allo step successivo valido. Nel frattempo non mostriamo nulla.
        return null
      case 'anagrafica':
        return <AnagraficaSection />
      case 'documento':
        return <DocumentoSection />
      case 'fornitura':
        return <FornituraSection />
      case 'pagamento':
        return <PagamentoSection />
      case 'riepilogo':
        if (mode === 'operator') {
          return <OperatorRiepilogoSection onSentToClient={onOperatorSentToClient} />
        }
        return <RiepilogoSection />
      case 'otp_request':
        return <OtpRequestSection />
      case 'otp_verify':
        return <OtpVerifySection />
      case 'firma':
        return <SigningSection />
      case 'completato':
        return <SigningComplete />
      default:
        return mode === 'self' ? <TipoClienteSection /> : <AnagraficaSection />
    }
  }
  
  const isSigningFlow = SIGNING_STEPS.includes(currentStep)

  const headerTitle = mode === 'operator'
    ? `Heracomm - Area Operatore${isBusiness ? ' (Business)' : ''}`
    : 'Heracomm - Attivazione Servizi'

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {!isSigningFlow && (
          <header className={styles.header}>
            <h1 className={styles.title}>
              {mode === 'operator' ? (
                headerTitle
              ) : (
                <>
                  <i className="fa-solid fa-bolt" aria-hidden />
                  {headerTitle}
                </>
              )}
            </h1>
            <p className={styles.subtitle}>
              {mode === 'operator' 
                ? 'Compila la pratica per il cliente'
                : 'Attiva la tua fornitura luce e gas in pochi passaggi'
              }
            </p>
          </header>
        )}

        {!isSigningFlow && (
          <StepIndicator
            steps={visibleSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            completedSteps={getCompletedSteps()}
          />
        )}

        <div className={styles.content} key={currentStep}>
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  )
}



